import { ChevronUpDownIcon, PlusIcon } from '@heroicons/react/16/solid';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { randomString } from '../../../functions/string.functions.ts';
import { useWorkspaceSwitch } from '../../../hooks/useWorkspaceSwitch.hook.ts';
import { useLayoutContext } from '../../../providers/LayoutProvider/Layout.context.ts';
import { useModalsContext } from '../../../providers/ModalsProvider/Modals.context.ts';
import { useWorkspaceContext } from '../../../providers/WorkspaceProvider/Workspace.context.ts';
import { tCurrentWorkspace } from '../../../providers/WorkspaceProvider/Workspace.types.ts';
import '../Sidebar.styles.scss';

export function SidebarWorkspaceSelect() {
  const { sidebarOpen } = useLayoutContext();
  const { setAddWorkspaceModalIsOpen } = useModalsContext();
  const { t } = useTranslation('sidebar/sidebarComponent');
  const { switchWorkspace } = useWorkspaceSwitch();

  const { workspaces, currentWorkspaceId, setWorkspaceSaleSource } =
    useWorkspaceContext();
  const [dropDownOpen, setDropDownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropDownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const workspace = workspaces?.workspaces.find(
    (workspace) => workspace.id === currentWorkspaceId,
  );
  const shortName = workspace !== null ? workspace?.name[0] : '';

  function selectWorkspace(workspace: tCurrentWorkspace) {
    switchWorkspace(workspace.id);
    setDropDownOpen(false);
  }

  return (
    <div ref={containerRef} className="relative group/workspace-select">
      <Dropdown
        className={classNames('Sidebar__WorkspaceSelect', {
          'dropdown-open': dropDownOpen,
        })}
        horizontal="right"
        vertical="top"
        color="neutral"
        data-testid="workspace_selector_dropdown"
      >
        <Dropdown.Toggle
          className="Sidebar__WorkspaceSelect__Toggle"
          color={'neutral'}
          data-testid="workspace_selector"
          onClick={(e) => { e.stopPropagation(); setDropDownOpen(!dropDownOpen); }}
        >
          {/* Lovable-style selector: tinted initial square + name + chevron
              inside one bordered card. Collapsed keeps only the square. */}
          <span className="Sidebar__WorkspaceSelect__Badge" aria-hidden="true">
            {shortName}
          </span>
          {/* Testid contract predates the card look: expanded exposes the
              full name, collapsed the initial — e2e helpers read this value. */}
          <span
            data-testid="workspace_selector_content"
            className={classNames('Sidebar__WorkspaceSelect__Name', {
              'sr-only': !sidebarOpen,
            })}
          >
            {sidebarOpen ? workspace?.name : shortName}
          </span>
          {sidebarOpen && (
            <ChevronUpDownIcon className="Sidebar__WorkspaceSelect__Chevron" />
          )}
        </Dropdown.Toggle>
        {dropDownOpen ? (
          <Dropdown.Menu
            className="Sidebar__WorkspaceSelect__Menu"
            data-testid="workspace_selector_menu"
          >
            {workspaces?.workspaces?.map((workspace) => (
              <Dropdown.Item
                key={randomString()}
                className={classNames('Sidebar__WorkspaceSelect__Item', {
                  'btn-active': workspace.id === currentWorkspaceId,
                })}
                data-testid="workspace_selector_item"
                onClick={() => selectWorkspace(workspace)}
              >
                <div className="Sidebar__WorkspaceSelect__ShortName">
                  {workspace.name[0]}
                </div>
                <p>{workspace.name}</p>
              </Dropdown.Item>
            ))}
            <Dropdown.Item
              onClick={() => {
                setWorkspaceSaleSource('expansion');
                setAddWorkspaceModalIsOpen(true);
              }}
              className="Sidebar__WorkspaceSelect__Item"
              data-testid="workspace_selector_add_item"
            >
              <div className="Sidebar__WorkspaceSelect__Add">
                <PlusIcon className="Sidebar__WorkspaceSelect__Add__Icon " />
              </div>
              <p>{t('page.workspaceSelect.createWorkspace')}</p>
            </Dropdown.Item>
          </Dropdown.Menu>
        ) : null}
      </Dropdown>
      {!sidebarOpen && (
        <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover/workspace-select:opacity-100">
          {t('page.workspaceSelect.workspace')}
        </span>
      )}
    </div>
  );
}
